// Etapa 2: Valores dos Itens

import React, { useState } from "react";
import {
  HiAnnotation,
  HiCalculator,
  HiCalendar,
  HiClock,
  HiCreditCard,
  HiDocumentText,
} from "react-icons/hi";
import { NumericFormat } from "react-number-format";
import {
  ItemOrcamento,
  DadosValores,
} from "../../../types/orcamento-fornecedor.types";
import { formatCurrency } from "../../../utils/orcamento-formatters";
import { CLAUSULA_CANCELAMENTO_PADRAO } from "../../../utils/orcamentoDefaults";

interface Etapa2Props {
  itens: ItemOrcamento[];
  dadosValores: Partial<DadosValores>;
  onDadosChange: (dados: Partial<DadosValores>) => void;
  errors: Record<string, string>;
  showErrors?: boolean;
}

const OPCOES_FIXAS_PAGAMENTO = [
  "Faturado 30 dias após o evento",
  "Faturado 60 dias após o evento",
];

export const Etapa2Valores: React.FC<Etapa2Props> = ({
  itens,
  dadosValores,
  onDadosChange,
  errors,
  showErrors = false,
}) => {
  const [opcaoPagamento, setOpcaoPagamento] = useState<string>(() => {
    const v = dadosValores.formaPagamento || "";
    if (!v) return "";
    return OPCOES_FIXAS_PAGAMENTO.includes(v) ? v : "Outra";
  });

  const handleOpcaoPagamento = (opcao: string) => {
    setOpcaoPagamento(opcao);
    if (opcao !== "Outra") {
      onDadosChange({ formaPagamento: opcao });
    } else {
      onDadosChange({ formaPagamento: "" });
    }
  };

  const calcularSubtotal = (item: ItemOrcamento) => {
    const valorUnitario = dadosValores.itens?.[item.id] || 0;
    return item.quantidade * item.diaria * valorUnitario;
  };

  const calcularTotal = () => {
    return itens.reduce((total, item) => total + calcularSubtotal(item), 0);
  };

  const handleValorChange = (itemId: string, valor: number) => {
    const novosItens = { ...(dadosValores.itens || {}) };
    novosItens[itemId] = valor;

    // Recalcular total
    const novoTotal = itens.reduce((total, item) => {
      const valorItem = novosItens[item.id] || 0;
      return total + item.quantidade * item.diaria * valorItem;
    }, 0);

    onDadosChange({
      itens: novosItens,
      totalGeral: novoTotal,
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 rounded-lg bg-[#4760e0]/10 flex items-center justify-center">
            <HiCalculator className="h-6 w-6 text-[#4760e0]" />
          </div>
          <h2 className="text-xl font-bold text-[#161519]">
            Itens para orçamento
          </h2>
        </div>

        <div className="overflow-x-auto">
          {showErrors && errors.itens && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                ⚠️ Atenção: preencha o valor unitário de todos os itens
                destacados em vermelho antes de continuar.
              </p>
            </div>
          )}
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Item
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Descrição
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                  Qtd
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                  Diárias
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  Valor Unitário
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  Subtotal
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, index) => {
                const isEditavel = item.editavel !== false;
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-100 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } ${!isEditavel ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-[#4760e0]">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {item.descricao}
                      </div>
                      {item.descritivo && (
                        <div className="text-sm text-gray-500 mt-1">
                          {item.descritivo}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">
                      {item.quantidade}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">
                      {item.diaria}
                    </td>
                    <td className="px-4 py-3">
                      {isEditavel ? (
                        <>
                          <NumericFormat
                            value={dadosValores.itens?.[item.id] || ""}
                            onValueChange={(values) => {
                              const valor = values.floatValue || 0;
                              handleValorChange(item.id, valor);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale={false}
                            allowNegative={false}
                            prefix="R$ "
                            placeholder="R$ 0,00"
                            className={`w-full px-3 py-2 rounded-lg border-2 transition-all outline-none text-sm ${
                              errors[item.id] ||
                              (showErrors &&
                                (!dadosValores.itens?.[item.id] ||
                                  dadosValores.itens[item.id] <= 0))
                                ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
                            }`}
                          />
                          {(errors[item.id] ||
                            (showErrors &&
                              (!dadosValores.itens?.[item.id] ||
                                dadosValores.itens[item.id] <= 0))) && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors[item.id] || "Informe o valor unitário"}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-500">
                            {formatCurrency(
                              dadosValores.itens?.[item.id] ||
                                item.valorUnitario ||
                                0,
                            )}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                            Bloqueado
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatCurrency(calcularSubtotal(item))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#4760e0] text-white">
                <td
                  colSpan={5}
                  className="px-4 py-4 text-right text-base font-bold"
                >
                  TOTAL GERAL:
                </td>
                <td className="px-4 py-4 text-right text-base font-bold">
                  {formatCurrency(calcularTotal())}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-[#161519] mb-6 pb-4 border-b border-gray-200">
          Informações do orçamento
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <HiCalendar className="h-5 w-5 text-[#4760e0]" />
                Data do orçamento <span className="text-red-500">*</span>
              </span>
            </label>
            <input
              type="date"
              value={dadosValores.dataOrcamento || ""}
              onChange={(e) => onDadosChange({ dataOrcamento: e.target.value })}
              className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none ${
                showErrors && errors.dataOrcamento
                  ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
              }`}
            />
            {showErrors && errors.dataOrcamento && (
              <p className="text-sm text-red-600 mt-1">
                {errors.dataOrcamento}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <HiClock className="h-5 w-5 text-[#4760e0]" />
                Validade (em dias) <span className="text-red-500">*</span>
              </span>
            </label>
            <input
              type="number"
              value={dadosValores.validadeDias || ""}
              onChange={(e) =>
                onDadosChange({ validadeDias: parseInt(e.target.value) || 0 })
              }
              className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none ${
                showErrors && errors.validadeDias
                  ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
              }`}
              placeholder="Ex: 30"
              min="30"
              max="365"
            />
            {showErrors && errors.validadeDias && (
              <p className="text-sm text-red-600 mt-1">{errors.validadeDias}</p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              Validade mínima de 30 dias. Informe por quantos dias este
              orçamento será válido.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <HiCreditCard className="h-5 w-5 text-[#4760e0]" />
                Forma de pagamento <span className="text-red-500">*</span>
              </span>
            </label>
            <select
              value={opcaoPagamento}
              onChange={(e) => handleOpcaoPagamento(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none bg-white ${
                showErrors && errors.formaPagamento && !opcaoPagamento
                  ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
              }`}
            >
              <option value="">Selecione uma opção...</option>
              {OPCOES_FIXAS_PAGAMENTO.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
              <option value="Outra">Outra</option>
            </select>

            {opcaoPagamento === "Outra" && (
              <div className="relative mt-3">
                <input
                  type="text"
                  value={dadosValores.formaPagamento || ""}
                  onChange={(e) =>
                    onDadosChange({ formaPagamento: e.target.value })
                  }
                  maxLength={200}
                  placeholder="Descreva a forma de pagamento"
                  className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none ${
                    showErrors && errors.formaPagamento
                      ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                      : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
                  }`}
                />
                <span className="absolute top-1/2 -translate-y-1/2 right-3 text-xs text-gray-400">
                  {(dadosValores.formaPagamento || "").length}/200
                </span>
              </div>
            )}

            {showErrors && errors.formaPagamento && (
              <p className="text-sm text-red-600 mt-1">
                {errors.formaPagamento}
              </p>
            )}
          </div>

          {/* Cláusula e observações eram um textarea só. Juntas, a condição
              contratual e o recado solto saíam no documento como um bloco em
              que não se distinguia uma da outra. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <HiDocumentText className="h-5 w-5 text-[#4760e0]" />
                Cláusula de cancelamento{" "}
                <span className="text-gray-400 text-xs font-normal">
                  (Opcional)
                </span>
              </span>
            </label>
            <div className="relative">
              <textarea
                value={dadosValores.clausulaCancelamento || ""}
                onChange={(e) =>
                  onDadosChange({ clausulaCancelamento: e.target.value })
                }
                maxLength={1000}
                rows={3}
                placeholder={CLAUSULA_CANCELAMENTO_PADRAO}
                className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none resize-none ${
                  showErrors && errors.clausulaCancelamento
                    ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                    : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
                }`}
              />
              <span className="absolute bottom-2 right-3 text-xs text-gray-400">
                {(dadosValores.clausulaCancelamento || "").length}/1000
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ajuste o prazo e o percentual conforme a sua condição.
            </p>
            {showErrors && errors.clausulaCancelamento && (
              <p className="text-sm text-red-600 mt-1">
                {errors.clausulaCancelamento}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <HiAnnotation className="h-5 w-5 text-[#4760e0]" />
                Observações{" "}
                <span className="text-gray-400 text-xs font-normal">
                  (Opcional)
                </span>
              </span>
            </label>
            <div className="relative">
              <textarea
                value={dadosValores.observacoesProposta || ""}
                onChange={(e) =>
                  onDadosChange({ observacoesProposta: e.target.value })
                }
                maxLength={1000}
                rows={3}
                placeholder="Ex: prazo de montagem, condições de acesso ao local, itens não inclusos."
                className={`w-full px-4 py-2.5 rounded-lg border-2 transition-all outline-none resize-none ${
                  showErrors && errors.observacoesProposta
                    ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                    : "border-gray-200 focus:border-[#4760e0] focus:ring-2 focus:ring-[#4760e0]/20"
                }`}
              />
              <span className="absolute bottom-2 right-3 text-xs text-gray-400">
                {(dadosValores.observacoesProposta || "").length}/1000
              </span>
            </div>
            {showErrors && errors.observacoesProposta && (
              <p className="text-sm text-red-600 mt-1">
                {errors.observacoesProposta}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
